import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { tv, type VariantProps } from 'tailwind-variants';

import { ReactComponent as SpinIcon } from '@cobliteam/ui/icons/loading/spin.svg';

import colors from '../../theme/tokens/colors';
import cn from '../../utils/cn';
import Loading from '../Loading';

export type BaseButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & VariantProps<typeof buttonVariants>;

export type Color = 'primary' | 'secondary' | 'success' | 'danger';

export type Variant = 'contained' | 'outlined' | 'text' | 'link' | 'ghost';

type IconProps = {
  icon?: React.ReactNode | any;
  startIcon?: never;
  endIcon?: never;
  children?: never;
};

type DefaultProps = {
  icon?: never;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children: React.ReactNode;
};

type StartIconProps = {
  icon?: never;
  startIcon: React.ReactNode;
  endIcon?: never;
  children: React.ReactNode;
};

type EndIconProps = {
  icon?: never;
  startIcon?: never;
  endIcon: React.ReactNode;
  children: React.ReactNode;
};

export type ButtonProps = BaseButtonProps &
  (IconProps | EndIconProps | StartIconProps | DefaultProps) & {
    asChild?: boolean;
    loading?: boolean;
    full?: boolean;
  };

const buttonVariants = tv({
  base: `
    flex items-center justify-center
    whitespace-nowrap rounded px-5 text-sm font-bold text-white
    outline-none focus-within:ring-0
    focus-within:ring-offset-0 hover:duration-500 hover:ease-in-out
    focus:outline-none focus:ring-0
    active:scale-95 active:ring-0 active:transition-all
    disabled:cursor-not-allowed disabled:active:scale-100`,
  variants: {
    color: {
      primary: 'border-primary bg-primary hover:bg-primary-dark disabled:bg-primary-light data-[state=open]:bg-primary-light',
      secondary: 'border-secondary bg-secondary hover:bg-secondary-dark disabled:bg-secondary-light data-[state=open]:bg-secondary-light',
      success: 'border-success bg-success hover:bg-success-dark disabled:bg-success-light data-[state=open]:bg-success-light',
      danger: 'border-danger bg-danger hover:bg-danger-dark disabled:bg-danger-light data-[state=open]:bg-danger-light',
    },
    variant: {
      contained: '',
      outlined: 'border',
      ghost: 'bg-transparent text-sm font-normal',
      text: 'bg-transparent',
      link: 'bg-transparent p-0',
    },
    icon: {
      true: 'p-3',
      false: '',
    },
    full: {
      true: 'w-full',
      false: '',
    },
    loading: {
      true: 'cursor-auto items-center justify-center hover:pointer-events-none',
      false: '',
    },
    size: {
      regular: 'h-10 px-5 leading-100',
      condensed: 'h-7 px-3 leading-100',
      auto: 'h-auto p-0 leading-100',
    },
  },
  compoundVariants: [
    {
      variant: 'outlined',
      className:
        'border-secondary-light bg-white text-secondary-dark hover:border-secondary-dark hover:bg-white hover:text-secondary-black disabled:border-secondary-light disabled:bg-secondary-lightness disabled:text-secondary-light',
    },
    {
      variant: 'text',
      color: 'primary',
      className: 'bg-transparent text-primary hover:bg-primary-light/10 hover:text-primary-dark disabled:bg-transparent disabled:text-primary-light',
    },
    {
      variant: 'text',
      color: 'secondary',
      className: 'bg-transparent text-secondary-dark hover:bg-secondary-light/10 hover:text-secondary-black disabled:bg-transparent disabled:text-secondary-lightness',
    },
    {
      variant: 'text',
      color: 'success',
      className: 'bg-transparent text-success hover:bg-success-light/10 hover:text-success-dark  disabled:bg-transparent disabled:text-success-light',
    },
    {
      variant: 'text',
      color: 'danger',
      className: 'bg-transparent text-danger hover:bg-danger-light/10 hover:text-danger-dark disabled:bg-transparent disabled:text-danger-light',
    },
    {
      variant: 'link',
      color: 'primary',
      className: 'bg-transparent p-0 text-primary hover:bg-transparent hover:text-primary-dark disabled:bg-transparent disabled:text-primary-light',
    },
    {
      variant: 'link',
      color: 'secondary',
      className: 'bg-transparent p-0 text-secondary-dark hover:bg-transparent hover:text-secondary-black disabled:bg-transparent disabled:text-secondary-light',
    },
    {
      variant: 'link',
      color: 'success',
      className: 'bg-transparent p-0 text-success hover:bg-transparent hover:text-success-dark disabled:bg-transparent disabled:text-success-light',
    },
    {
      variant: 'link',
      color: 'danger',
      className: 'bg-transparent p-0 text-danger hover:bg-transparent hover:text-danger-dark disabled:bg-transparent disabled:text-danger-light',
    },
    {
      variant: 'ghost',
      color: 'primary',
      className: 'rounded-none bg-transparent p-0 text-primary hover:bg-transparent hover:text-primary-dark disabled:bg-transparent disabled:text-primary-light',
    },
    {
      variant: 'ghost',
      color: 'secondary',
      className: 'rounded-none bg-transparent p-0 text-secondary-dark hover:bg-transparent hover:text-secondary-black disabled:bg-transparent disabled:text-secondary-light',
    },
    {
      variant: 'ghost',
      color: 'success',
      className: 'rounded-none bg-transparent p-0 text-success hover:bg-transparent hover:text-success-dark disabled:bg-transparent disabled:text-success-light',
    },
    {
      variant: 'ghost',
      color: 'danger',
      className: 'rounded-none bg-transparent p-0 text-danger hover:bg-transparent hover:text-danger-dark disabled:bg-transparent disabled:text-danger-light',
    },
    {
      icon: true,
      size: 'regular',
      className: 'p-3',
    },
    {
      icon: true,
      variant: 'contained',
      size: 'condensed',
      className: 'p-1.5',
    },
    {
      icon: true,
      variant: 'outlined',
      color: 'secondary',
      size: 'regular',
      className: 'border-none p-3 inner-border inner-border-secondary-light hover:inner-border-secondary-dark hover:disabled:inner-border-secondary',
    },
    {
      icon: true,
      variant: 'outlined',
      color: 'secondary',
      size: 'condensed',
      className: 'border-none p-1.5 inner-border inner-border-secondary-light hover:inner-border-secondary-dark hover:disabled:inner-border-secondary-light',
    },
  ],
  defaultVariants: {
    size: 'regular',
    color: 'primary',
    icon: false,
  },
});

const LoadingBullets = ({
  color = 'primary',
  variant = 'contained',
  size = 'regular',
}: {
  color?: Color;
  variant?: Variant | undefined | null;
  size?: 'regular' | 'condensed' | 'auto';
}) => {
  const fillColor = {
    primary: colors.primary.DEFAULT,
    secondary: colors.secondary.DEFAULT,
    success: colors.success.DEFAULT,
    danger: colors.danger.DEFAULT,
  } as const;

  const fill = {
    contained: colors.white.DEFAULT,
    outlined: colors.secondary.dark,
    text: fillColor[color],
    link: fillColor[color],
    ghost: fillColor[color],
  }[variant as Variant];

  return (
    <div className='relative mx-auto flex h-2 w-12 items-center justify-center'>
      <Loading className={cn('absolute -top-0.5', size === 'condensed' && '!w-8')} fill={fill} />
    </div>
  );
};

interface CustomIconProps {
  icon: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  position?: 'start' | 'end';
}

const customIcon = ({ icon }: CustomIconProps) => {
  return React.cloneElement(icon as React.ReactElement, {
    className: cn((icon as React.ReactElement)?.props?.className || 'w-4 h-4'),
  });
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'contained',
      type = 'button',
      size = 'regular',
      loading = false,
      full = false,
      asChild = false,
      icon = false,
      startIcon,
      color,
      endIcon,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    const buttonProps = {
      className: cn(
        buttonVariants({
          variant,
          color,
          size,
          icon: !!icon,
          full,
          className,
        })
      ),
      type,
      ref,
      ...props,
    };

    const iconProps: CustomIconProps = {
      icon,
      children,
    };

    const startIconProps: CustomIconProps = {
      icon: startIcon,
      children,
      position: 'start',
    };

    const endIconProps: CustomIconProps = {
      icon: endIcon,
      children,
      position: 'end',
    };

    const renderIconAndLoading = () => {
      return (
        <Comp {...buttonProps}>
          <div className='flex h-full flex-row items-center justify-center gap-2.5'>
            {!loading && startIcon ? customIcon(startIconProps) : null}
            {loading && !asChild ? <LoadingBullets color={color} variant={variant} size={size} /> : children}
            {!loading && endIcon ? customIcon(endIconProps) : null}
          </div>
        </Comp>
      );
    };

    const renderOnlyLoading = () => {
      return (
        <Comp {...buttonProps}>
          <LoadingBullets color={color} variant={variant} size={size} />
        </Comp>
      );
    };

    const renderOnlyIcon = () => {
      return (
        <Comp {...buttonProps}>
          <div className='flex h-full flex-row items-center justify-center'>{loading ? <SpinIcon data-testid='loading' className='h-4 w-4' /> : customIcon(iconProps)}</div>
        </Comp>
      );
    };

    if (icon && !children) return renderOnlyIcon();

    if (!!startIcon || !!endIcon) return renderIconAndLoading();

    if (loading && !asChild) return renderOnlyLoading();

    return <Comp {...buttonProps}>{children}</Comp>;
  }
);

export default Button;
